// =====================================================================
// commande-prete/tempsEncodage.js — panneau « ⏱ Temps d'encodage » (27-10-2026, demande Silviu)
// =====================================================================
// Du scan de la référence au 1er mail « prête » envoyé (calcul du serveur : GET /ready-orders/stats/encodage,
// droit « Administrer les commandes prêtes »). Fonctions PURES : testées par frontend/test/temps-encodage.test.mjs.
// =====================================================================

// Premier jour mesuré (v3, 16-10-2026) : avant, les commandes n'ont pas de début d'encodage.
export const DEBUT_MESURE = '2026-10-16';

// Le serveur refuse plus de 366 jours (MAX_JOURS_STATS) : l'écran ne propose jamais plus.
export const MAX_JOURS = 366;

export const PERIODES = Object.freeze([
  { cle: 'jour', libelle: "Aujourd'hui" },
  { cle: '7j', libelle: '7 jours' },
  { cle: 'tout', libelle: 'Depuis le 16/10' },   // (au plus MAX_JOURS jours : un an glissant après le 16/10/2027)
]);

const ymd = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

/** Aujourd'hui à Bruxelles, AAAA-MM-JJ. */
export function aujourdhuiBxl(maintenant = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).format(maintenant);
}

export const decaler = (jour, n) => { const [a, m, j] = jour.split('-').map(Number); return ymd(new Date(Date.UTC(a, m - 1, j + n))); };
export const auPlusTot = (du) => (du < DEBUT_MESURE ? DEBUT_MESURE : du);

/** { du, au } d'une période prédéfinie ; jamais avant DEBUT_MESURE ni plus de MAX_JOURS jours. */
export function bornes(cle, maintenant = new Date()) {
  const au = aujourdhuiBxl(maintenant);
  if (cle === 'jour') return { du: au, au };
  if (cle === '7j') return { du: auPlusTot(decaler(au, -6)), au };
  return { du: auPlusTot(decaler(au, -(MAX_JOURS - 1))), au };
}

/** Dernier jour permis pour « au » quand « du » est choisi (MAX_JOURS jours au plus). */
export function auMax(du) { return du ? decaler(du, MAX_JOURS - 1) : undefined; }

/** Période choisie à la main : message humain si elle est impossible, sinon null. */
export function erreurPeriode(du, au) {
  if (!du || !au) return 'Choisissez les deux dates.';
  if (du > au) return 'La date « du » est après la date « au ».';
  if (au > auMax(du)) return `Période trop longue : ${MAX_JOURS} jours au plus.`;
  return null;
}

/** 4 → « 4 min », 75 → « 1 h 15 », 1028 → « 17 h 08 ». */
export function duree(minutes) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/** 3 palettes + 1 colis → « 3 pal. · 1 col. » ; rien → « — ». */
export function unites(palettes, colis) {
  const p = Number(palettes) || 0; const c = Number(colis) || 0;
  const morceaux = [p ? `${p} pal.` : '', c ? `${c} col.` : ''].filter(Boolean);
  return morceaux.length ? morceaux.join(' · ') : '—';
}

/** Minutes par palette / colis → « 2,5 min/u » (virgule belge) ; null → « — ». */
export function parUnite(minutes) {
  if (minutes === null || minutes === undefined || !Number.isFinite(Number(minutes))) return '—';
  return `${String(Math.round(Number(minutes) * 10) / 10).replace('.', ',')} min/u`;
}

/** D'où vient la proposition (calculée par le serveur) : texte pour l'admin. */
export function texteProposition(p, minCommandes = 5) {
  if (!p) return '';
  const base = parUnite(p.parUnite);
  return p.source === 'operateur'
    ? `son temps habituel : ${base} sur ${p.nbHistorique} commandes`
    : `temps de l'équipe : ${base} (${p.nbOperateur} commande${p.nbOperateur > 1 ? 's' : ''} normale${p.nbOperateur > 1 ? 's' : ''} pour lui, ${minCommandes} nécessaires)`;
}

/** Saisie « Autre temps… » : message humain si impossible, sinon null (le serveur refait les mêmes contrôles). */
export function erreurCorrection(minutesTexte, motif, mesure) {
  const t = String(minutesTexte ?? '').trim();
  if (!/^\d+$/.test(t)) return 'Temps facturable : un nombre entier de minutes.';
  if (Number(t) > mesure) return `Au plus le temps mesuré (${mesure} min).`;
  if (String(motif ?? '').trim().length < 3) return 'Motif obligatoire : dites en quelques mots pourquoi.';
  if (String(motif).trim().length > 300) return 'Motif : 300 caractères au plus.';
  return null;
}

/** Toutes les commandes de la réponse, à plat, les plus récentes d'abord, avec le nom de l'opérateur. */
export function commandes(data, client = null) {
  if (!data) return [];
  return data.utilisateurs.flatMap((u) => u.detail.map((d) => ({ ...d, operateur: u.nom })))
    .filter((d) => !client || (d.client || '—') === client)
    .sort((x, y) => String(y.fin).localeCompare(String(x.fin)));
}
